import { useEffect, useMemo, useState } from "react";
import { Box, Button, TextField, Typography, useTheme } from "@mui/material";
import TerminalIcon from "@mui/icons-material/Terminal";
import MonitorIcon from "@mui/icons-material/Monitor";
import RouterIcon from "@mui/icons-material/Router";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { SkeletonLoader } from "../../../modules/dashboard/shared";

type Protocol = "ssh" | "wmi" | "snmp";
type PortsByProtocol = Record<Protocol, number[]>;
type DraftsByProtocol = Record<Protocol, string>;
type ErrorsByProtocol = Record<Protocol, string>;

const EMPTY_PORTS: PortsByProtocol = { ssh: [], wmi: [], snmp: [] };
const EMPTY_DRAFTS: DraftsByProtocol = { ssh: "", wmi: "", snmp: "" };
const EMPTY_ERRORS: ErrorsByProtocol = { ssh: "", wmi: "", snmp: "" };

// Per-protocol config -- not state. Defaults mirror what discovery actually probes
// unconditionally today (SSH: scan_ports.py's REMAPPED_MGMT_PORTS; WMI: the fixed
// probe list in enrich_device.py; SNMP: the hardcoded port in snmp_probe.py), so the
// copy here and the "already checked by default" validation stay truthful.
const MAX_CUSTOM_PORTS_PER_PROTOCOL = 50;

const PROTOCOL_DEFS: {
  key: Protocol;
  name: string;
  Icon: typeof TerminalIcon;
  blurb: string;
  defaults: number[];
}[] = [
  {
    key: "ssh",
    name: "SSH",
    Icon: TerminalIcon,
    blurb: "Shell access on Linux, network gear and appliances. Probed on 22 by default.",
    defaults: [22],
  },
  {
    key: "wmi",
    name: "Windows",
    Icon: MonitorIcon,
    blurb: "Windows management and remote instrumentation. Probed on 445, 3389, 5985 and 5986 by default.",
    defaults: [445, 3389, 5985, 5986],
  },
  {
    key: "snmp",
    name: "SNMP",
    Icon: RouterIcon,
    blurb: "Switches, printers, UPS and other polled devices. Probed on 161 by default.",
    defaults: [161],
  },
];

function useTokens() {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  return {
    dark,
    pageBg: dark ? theme.palette.background.default : "#f6f6fb",
    surface: dark ? "#191C24" : "#ffffff",
    footerSurface: dark ? "rgba(255,255,255,0.03)" : "#fafafd",
    borderStrong: dark ? "rgba(255,255,255,0.14)" : "#e8e8f2",
    borderHairline: dark ? "rgba(255,255,255,0.08)" : "#f0f0f7",
    inputBorder: dark ? "rgba(255,255,255,0.23)" : "#dedeeb",
    textPrimary: dark ? "#ffffff" : "#1b1b29",
    textSecondary: dark ? "#b3b3b3" : "#62627d",
    textTertiary: dark ? "#9a9ab5" : "#8a8aa3",
    textLabel: dark ? "#8a8aa5" : "#a3a3ba",
    textMuted: dark ? "#7a7a90" : "#b0b0c4",
    brand: theme.palette.primary.main,
    brandHover: theme.palette.primary.light,
    brandDeep: dark ? "#b8aeff" : "#3325b0",
    brandDeepHover: dark ? "#cfc7ff" : "#2a1fa8",
    chipFill: dark ? "rgba(58,42,218,0.18)" : "#eeebfd",
    chipBorder: dark ? "rgba(58,42,218,0.4)" : "#ddd7fb",
    chipHoverFill: dark ? "rgba(58,42,218,0.3)" : "#ddd7fb",
    ghostHover: dark ? "rgba(255,255,255,0.06)" : "#f7f5ff",
    neutralHover: dark ? "rgba(255,255,255,0.06)" : "#eeeef6",
    errorText: dark ? "#ff9bb0" : "#c2405a",
    focusRing: "rgba(79,57,217,0.18)",
  };
}

const MONO_FONT = "'JetBrains Mono', monospace";

function validatePort(
  raw: string,
  def: (typeof PROTOCOL_DEFS)[number],
  currentPorts: number[]
): { n: number; error: string } {
  const value = raw.trim();
  if (!value) return { n: NaN, error: "Enter a port number." };
  const n = Number(value);
  if (!Number.isInteger(n) || n < 1 || n > 65535) {
    return { n, error: "Ports run from 1 to 65535." };
  }
  if (def.defaults.includes(n)) {
    return { n, error: `${n} is already checked by default.` };
  }
  if (currentPorts.includes(n)) {
    return { n, error: `${n} is already in your list.` };
  }
  if (currentPorts.length >= MAX_CUSTOM_PORTS_PER_PROTOCOL) {
    return { n, error: `You've reached the limit of ${MAX_CUSTOM_PORTS_PER_PROTOCOL} custom ports for ${def.name}.` };
  }
  return { n, error: "" };
}

function ProtocolRow({
  def,
  ports,
  draft,
  error,
  onDraftChange,
  onAdd,
  onRemove,
}: {
  def: (typeof PROTOCOL_DEFS)[number];
  ports: number[];
  draft: string;
  error: string;
  onDraftChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (port: number) => void;
}) {
  const t = useTokens();
  const Icon = def.Icon;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "380px 1fr" },
        gap: { xs: "12px", sm: "24px" },
        padding: "18px 28px",
        borderTop: `1px solid ${t.borderHairline}`,
        alignItems: "center",
      }}
    >
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Icon sx={{ fontSize: 20, flex: "none", color: t.textPrimary }} />
          <Typography component="h2" sx={{ fontSize: "15px", fontWeight: 600, color: t.textPrimary }}>
            {def.name}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "12.5px",
            lineHeight: 1.55,
            color: t.textTertiary,
            margin: "6px 0 0",
          }}
        >
          {def.blurb}
        </Typography>
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
            {ports.map((port) => (
              <Box
                key={port}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontFamily: MONO_FONT,
                  fontSize: "12.5px",
                  padding: "5px 6px 5px 10px",
                  borderRadius: "6px",
                  backgroundColor: t.chipFill,
                  color: t.brandDeep,
                  border: `1px solid ${t.chipBorder}`,
                }}
              >
                {port}
                <Box
                  component="button"
                  type="button"
                  title="Remove port"
                  onClick={() => onRemove(port)}
                  sx={{
                    border: "none",
                    background: "transparent",
                    color: t.brandDeep,
                    fontSize: "14px",
                    lineHeight: 1,
                    padding: "2px 4px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                    "&:hover": { backgroundColor: t.chipHoverFill, color: t.brandDeepHover },
                    "&:focus-visible": { outline: `2px solid ${t.brand}`, outlineOffset: "1px" },
                  }}
                >
                  ×
                </Box>
              </Box>
            ))}

            {ports.length === 0 && (
              <Typography component="span" sx={{ fontSize: "12.5px", color: t.textMuted, whiteSpace: "nowrap" }}>
                No extra ports
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <TextField
              size="small"
              placeholder="Port number"
              inputMode="numeric"
              value={draft}
              onChange={(e) => onDraftChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAdd();
                }
              }}
              sx={{
                width: "122px",
                "& .MuiOutlinedInput-root": {
                  fontFamily: MONO_FONT,
                  fontSize: "12.5px",
                  backgroundColor: t.surface,
                  borderRadius: "7px",
                  "& fieldset": { borderColor: t.inputBorder },
                  "&:hover fieldset": { borderColor: t.inputBorder },
                  "&.Mui-focused fieldset": {
                    borderColor: t.brand,
                    borderWidth: "1px",
                    boxShadow: `0 0 0 3px ${t.focusRing}`,
                  },
                },
                "& .MuiInputBase-input": { padding: "7px 10px", color: t.textPrimary },
              }}
            />
            <Button
              onClick={onAdd}
              variant="outlined"
              sx={{
                fontSize: "12.5px",
                fontWeight: 500,
                padding: "7px 13px",
                borderRadius: "7px",
                borderColor: t.inputBorder,
                color: t.brandDeep,
                whiteSpace: "nowrap",
                "&:hover": { borderColor: t.brand, backgroundColor: t.ghostHover },
              }}
            >
              Add
            </Button>
          </Box>
        </Box>

        {error && (
          <Typography sx={{ margin: "8px 0 0", fontSize: "12px", color: t.errorText }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

const CustomPorts = () => {
  const t = useTokens();
  const [ports, setPorts] = useState<PortsByProtocol>(EMPTY_PORTS);
  const [savedPorts, setSavedPorts] = useState<PortsByProtocol>(EMPTY_PORTS);
  const [drafts, setDrafts] = useState<DraftsByProtocol>(EMPTY_DRAFTS);
  const [errors, setErrors] = useState<ErrorsByProtocol>(EMPTY_ERRORS);
  const [isLoading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(END_POINTS.ORG_PORT_HINTS);
      if (response.status === 200) {
        const fetched = response.data?.data?.ports || {};
        const next: PortsByProtocol = {
          ssh: fetched.ssh || [],
          wmi: fetched.wmi || [],
          snmp: fetched.snmp || [],
        };
        setPorts(next);
        setSavedPorts(next);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to load custom ports", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isDirty = useMemo(() => JSON.stringify(ports) !== JSON.stringify(savedPorts), [ports, savedPorts]);
  const totalCustom = useMemo(
    () => PROTOCOL_DEFS.reduce((sum, def) => sum + ports[def.key].length, 0),
    [ports]
  );

  const handleDraftChange = (key: Protocol, value: string) => {
    setDrafts((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleAdd = (def: (typeof PROTOCOL_DEFS)[number]) => {
    const { n, error } = validatePort(drafts[def.key], def, ports[def.key]);
    if (error) {
      setErrors((prev) => ({ ...prev, [def.key]: error }));
      return;
    }
    setPorts((prev) => ({
      ...prev,
      [def.key]: [...prev[def.key], n].sort((a, b) => a - b),
    }));
    setDrafts((prev) => ({ ...prev, [def.key]: "" }));
    setErrors((prev) => ({ ...prev, [def.key]: "" }));
  };

  const handleRemove = (key: Protocol, port: number) => {
    setPorts((prev) => ({ ...prev, [key]: prev[key].filter((p) => p !== port) }));
  };

  const handleDiscard = () => {
    setPorts(savedPorts);
    setDrafts(EMPTY_DRAFTS);
    setErrors(EMPTY_ERRORS);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axiosInstance.post(END_POINTS.ORG_PORT_HINTS, { ports });
      if (response.status === 200) {
        setSavedPorts(ports);
        toast.success("Custom ports saved", { type: "success" });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to save custom ports", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ padding: "40px 24px", maxWidth: "1120px", margin: "0 auto" }}>
        <SkeletonLoader count={6} styles={{ height: 40 }} />
      </Box>
    );
  }

  const allEmpty = totalCustom === 0;

  return (
    <Box sx={{ backgroundColor: t.pageBg, minHeight: "100%", width: "100%" }}>
      <Box sx={{ maxWidth: "1120px", margin: "0 auto", padding: "40px 24px 96px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: "32px",
            marginBottom: "28px",
          }}
        >
          {/* No page heading here -- the app header already shows "Custom Ports" as the
              page title (same convention as Credentials/VPN, neither of which repeats
              its own title in the body); a second "Custom ports" heading here would just
              duplicate it. */}
          <Typography sx={{ fontSize: "14px", lineHeight: 1.6, color: t.textSecondary, margin: 0, maxWidth: "620px" }}>
            Discovery checks a fixed set of ports per protocol. Add the non-default ports your
            fleet actually uses and both discovery and scans will check them, organization-wide.
          </Typography>

          {allEmpty ? (
            <Typography sx={{ fontSize: "12.5px", color: t.textMuted, whiteSpace: "nowrap" }}>
              No custom ports yet
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 14px",
                backgroundColor: t.surface,
                border: `1px solid ${t.borderStrong}`,
                borderRadius: "10px",
                whiteSpace: "nowrap",
              }}
            >
              <Typography component="span" sx={{ fontFamily: MONO_FONT, fontSize: "18px", fontWeight: 500, color: t.brand }}>
                {totalCustom}
              </Typography>
              <Typography component="span" sx={{ fontSize: "12px", lineHeight: 1.3, color: t.textSecondary }}>
                custom ports
                <br />
                across 3 protocols
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: t.surface,
            border: `1px solid ${t.borderStrong}`,
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          {PROTOCOL_DEFS.map((def) => (
            <ProtocolRow
              key={def.key}
              def={def}
              ports={ports[def.key]}
              draft={drafts[def.key]}
              error={errors[def.key]}
              onDraftChange={(value) => handleDraftChange(def.key, value)}
              onAdd={() => handleAdd(def)}
              onRemove={(port) => handleRemove(def.key, port)}
            />
          ))}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: "20px",
              padding: "16px 28px",
              backgroundColor: t.footerSurface,
              borderTop: `1px solid ${t.borderHairline}`,
            }}
          >
            <Typography sx={{ fontSize: "12.5px", color: t.textTertiary }}>
              Applies organization-wide, starting with the next scheduled discovery.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Button
                onClick={handleDiscard}
                disabled={!isDirty || isSaving}
                sx={{
                  color: t.textSecondary,
                  fontSize: "13px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: t.neutralHover },
                  "&.Mui-disabled": { color: t.textMuted, backgroundColor: "transparent" },
                }}
              >
                Discard
              </Button>
              <Button
                onClick={handleSave}
                disabled={!isDirty || isSaving}
                variant="contained"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  padding: "9px 18px",
                  borderRadius: "8px",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                  "&.Mui-disabled": {
                    backgroundColor: t.dark ? "rgba(255,255,255,0.1)" : "#e9e9f0",
                    color: t.textMuted,
                    opacity: 1,
                  },
                }}
              >
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomPorts;
